import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';

const STEPS = [
  { key: 'direction', label: '1. Setup' },
  { key: 'select', label: '2. Select' },
  { key: 'validate', label: '3. Validate' },
  { key: 'deploy', label: '4. Deploy' },
  { key: 'history', label: 'History' },
];

const isRequiredField = (f) => f?.isRequired === true || f?.is_required === true;

const SCENARIOS = [
  {
    id: 'full_clone',
    title: 'Full clone (sara data)',
    desc: 'Structure + config + saara CRM data. 1 DB → 2 DB ke liye best — owners as-is rehte hain.',
    deployMode: 'full_clone',
    preferDirection: 'prod',
    includeData: true,
    includeMeta: true,
  },
  {
    id: 'merge',
    title: 'Selective merge',
    desc: 'Sirf jo select karo wahi jayega. Existing rows skip. Safe incremental deploy.',
    deployMode: 'merge',
    preferDirection: 'same',
    includeData: false,
    includeMeta: false,
  },
  {
    id: 'replace_data',
    title: 'Replace data (truncate)',
    desc: 'Target pe selected tables truncate karke source se naya data daalo. Confirm zaroori.',
    deployMode: 'replace_data',
    preferDirection: 'prod',
    includeData: true,
    includeMeta: false,
  },
  {
    id: 'structure_only',
    title: 'Structure only',
    desc: 'Sirf tables/columns (DDL). Config aur record data nahi.',
    deployMode: 'structure_only',
    preferDirection: 'prod',
    includeData: false,
    includeMeta: false,
  },
];

function statusBadge(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'missing') return <Badge bg="warning" text="dark">missing</Badge>;
  if (s === 'partial') return <Badge bg="info">partial</Badge>;
  if (s === 'type_mismatch') return <Badge bg="danger">type mismatch</Badge>;
  if (s === 'same') return <Badge bg="success">same</Badge>;
  if (s === 'success') return <Badge bg="success">success</Badge>;
  if (s === 'failed' || s === 'rolled_back') return <Badge bg="danger">{s}</Badge>;
  return <Badge bg="secondary">{status || '—'}</Badge>;
}

/**
 * Salesforce-style Deploy Wizard — scenarios, select-all, validate, history.
 * Legacy migration buttons remain unchanged.
 */
export default function AdminDeployWizardModal({
  show,
  onHide,
  companyId,
  tenantcode = '',
  defaultSourceSchema = '',
  selectedTargetUserId = null,
}) {
  const [step, setStep] = useState('direction');
  const [schemas, setSchemas] = useState([]);
  const [scenarioId, setScenarioId] = useState('merge');
  const [direction, setDirection] = useState('same');
  const [deployMode, setDeployMode] = useState('merge');
  const [truncateConfirmed, setTruncateConfirmed] = useState(false);
  const [sourceSchema, setSourceSchema] = useState(defaultSourceSchema || '');
  const [targetSchema, setTargetSchema] = useState(tenantcode || '');
  const [compare, setCompare] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [objectChecked, setObjectChecked] = useState({});
  const [fieldChecked, setFieldChecked] = useState({});
  const [objectFlags, setObjectFlags] = useState({});
  const [includeReports, setIncludeReports] = useState(false);
  const [includeDashboards, setIncludeDashboards] = useState(false);
  const [includeHomeLayouts, setIncludeHomeLayouts] = useState(false);
  const [includeRoles, setIncludeRoles] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState({});
  const [selectedDashboardIds, setSelectedDashboardIds] = useState({});
  const [selectedHomeIds, setSelectedHomeIds] = useState({});
  const [selectedRoleIds, setSelectedRoleIds] = useState({});
  const [metaExpanded, setMetaExpanded] = useState({
    reports: false,
    dashboards: false,
    homes: false,
    roles: false,
  });
  const [validation, setValidation] = useState(null);
  const [validateLoading, setValidateLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [objectFilter, setObjectFilter] = useState('');
  const [sourceAdminUsers, setSourceAdminUsers] = useState([]);
  const [sourceUserId, setSourceUserId] = useState('');
  const [sourceAdminsLoading, setSourceAdminsLoading] = useState(false);
  /** Per-field CRM type pick: { "account.currency": "source"|"target" } */
  const [typeResolutions, setTypeResolutions] = useState({});
  /** Bulk override for unresolved mismatches: "source" | "target" | "" */
  const [typeResolutionForce, setTypeResolutionForce] = useState('');

  const applyScenario = (sc) => {
    setScenarioId(sc.id);
    setDeployMode(sc.deployMode);
    if (sc.preferDirection) setDirection(sc.preferDirection);
    setTruncateConfirmed(false);
    setIncludeReports(!!sc.includeMeta);
    setIncludeDashboards(!!sc.includeMeta);
    setIncludeHomeLayouts(!!sc.includeMeta);
    setIncludeRoles(!!sc.includeMeta);
  };

  useEffect(() => {
    if (!show) return;
    setStep('direction');
    setError('');
    setResult(null);
    setCompare(null);
    setValidation(null);
    setExpanded({});
    setObjectChecked({});
    setFieldChecked({});
    setObjectFlags({});
    setTruncateConfirmed(false);
    applyScenario(SCENARIOS[1]); // merge default
    setSourceAdminUsers([]);
    setSourceUserId('');
    setTypeResolutions({});
    setTypeResolutionForce('');
    setIncludeRoles(false);
    setSelectedReportIds({});
    setSelectedDashboardIds({});
    setSelectedHomeIds({});
    setSelectedRoleIds({});
    setMetaExpanded({ reports: false, dashboards: false, homes: false, roles: false });
    setSourceSchema(defaultSourceSchema || '');
    setTargetSchema(tenantcode || '');
    (async () => {
      try {
        const list = await adminSchemaApi.fetchMigrationSchemas();
        setSchemas(Array.isArray(list) ? list : []);
      } catch {
        setSchemas([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, defaultSourceSchema, tenantcode]);

  useEffect(() => {
    if (direction === 'prod' && sourceSchema) setTargetSchema(sourceSchema);
    else if (direction === 'same' && tenantcode) setTargetSchema(tenantcode);
  }, [direction, sourceSchema, tenantcode]);

  const loadSourceAdminUsers = useCallback(async (schema) => {
    if (!schema) {
      setSourceAdminUsers([]);
      setSourceUserId('');
      return;
    }
    setSourceAdminsLoading(true);
    try {
      const users = await adminSchemaApi.fetchSourceAdminUsers(schema);
      setSourceAdminUsers(Array.isArray(users) ? users : []);
      setSourceUserId((prev) => {
        if (prev && (users || []).some((u) => String(u.id) === String(prev))) return prev;
        return users?.[0]?.id ? String(users[0].id) : '';
      });
    } catch {
      setSourceAdminUsers([]);
      setSourceUserId('');
    } finally {
      setSourceAdminsLoading(false);
    }
  }, []);

  const initSelectionFromCompare = useCallback(
    (data, mode, withData) => {
      const nextObj = {};
      const nextFields = {};
      const nextFlags = {};
      const autoSelectAll = mode === 'full_clone' || mode === 'replace_data';
      for (const obj of data?.objects || []) {
        nextObj[obj.objectName] = autoSelectAll;
        nextFlags[obj.objectName] = {
          includeStructure: true,
          includeConfig: mode !== 'structure_only',
          includeData: withData || mode === 'full_clone' || mode === 'replace_data',
        };
        const fmap = {};
        for (const f of obj.fields || []) {
          fmap[f.fieldName] = autoSelectAll || f.status === 'missing' || isRequiredField(f);
        }
        nextFields[obj.objectName] = fmap;
      }
      setObjectChecked(nextObj);
      setFieldChecked(nextFields);
      setObjectFlags(nextFlags);
    },
    []
  );

  const initMetadataSelection = useCallback((data, selectAll) => {
    const items = data?.metadataItems || {};
    const toMap = (arr, on) => {
      const m = {};
      for (const row of arr || []) {
        if (row?.id) m[row.id] = on;
      }
      return m;
    };
    setSelectedReportIds(toMap(items.reports, selectAll));
    setSelectedDashboardIds(toMap(items.dashboards, selectAll));
    setSelectedHomeIds(toMap(items.homes, selectAll));
    setSelectedRoleIds(toMap(items.roles, selectAll));
    if (selectAll) {
      setIncludeReports((items.reports || []).length > 0);
      setIncludeDashboards((items.dashboards || []).length > 0);
      setIncludeHomeLayouts((items.homes || []).length > 0);
      setIncludeRoles((items.roles || []).length > 0);
    }
  }, []);

  const selectedIdsFromMap = (map) =>
    Object.entries(map || {})
      .filter(([, v]) => v)
      .map(([id]) => id);

  const buildPackageSelection = useCallback(() => {
    const objects = [];
    for (const [objectName, checked] of Object.entries(objectChecked)) {
      if (!checked) continue;
      const fieldsMap = fieldChecked[objectName] || {};
      const obj = (compare?.objects || []).find((o) => o.objectName === objectName);
      const fieldSet = new Set(
        Object.entries(fieldsMap)
          .filter(([, v]) => v)
          .map(([fn]) => fn)
      );
      for (const f of obj?.fields || []) {
        if (isRequiredField(f)) fieldSet.add(f.fieldName);
      }
      const fieldNames = [...fieldSet];
      const flags = objectFlags[objectName] || {};
      objects.push({
        objectName,
        fieldNames,
        includeStructure: flags.includeStructure !== false,
        includeConfig: flags.includeConfig !== false,
        includeData: flags.includeData === true,
      });
    }
    const reportIds = selectedIdsFromMap(selectedReportIds);
    const dashboardIds = selectedIdsFromMap(selectedDashboardIds);
    const homeIds = selectedIdsFromMap(selectedHomeIds);
    const roleIds = selectedIdsFromMap(selectedRoleIds);
    return {
      direction,
      deployMode,
      truncateConfirmed,
      sourceSchema,
      targetSchema: direction === 'prod' ? sourceSchema : targetSchema,
      selectedTargetUserId,
      sourceUserId:
        direction === 'same' && compare?.targetSchemaExists === false ? sourceUserId || null : null,
      objects,
      includeReports: includeReports && reportIds.length > 0,
      includeDashboards: includeDashboards && dashboardIds.length > 0,
      includeHomeLayouts: includeHomeLayouts && homeIds.length > 0,
      includeRoles: includeRoles && roleIds.length > 0,
      selectedReportIds: includeReports ? reportIds : [],
      selectedDashboardIds: includeDashboards ? dashboardIds : [],
      selectedHomeIds: includeHomeLayouts ? homeIds : [],
      selectedRoleIds: includeRoles ? roleIds : [],
      typeResolutions,
      typeResolutionForce: typeResolutionForce || null,
    };
  }, [
    objectChecked,
    fieldChecked,
    objectFlags,
    direction,
    deployMode,
    truncateConfirmed,
    sourceSchema,
    targetSchema,
    selectedTargetUserId,
    sourceUserId,
    compare?.targetSchemaExists,
    compare?.objects,
    includeReports,
    includeDashboards,
    includeHomeLayouts,
    includeRoles,
    selectedReportIds,
    selectedDashboardIds,
    selectedHomeIds,
    selectedRoleIds,
    typeResolutions,
    typeResolutionForce,
  ]);

  const runCompare = async () => {
    setCompareLoading(true);
    setError('');
    try {
      const data = await adminSchemaApi.wizardCompare({
        companyId,
        sourceSchema,
        targetSchema: direction === 'prod' ? sourceSchema : targetSchema,
        direction,
      });
      setCompare(data);
      initSelectionFromCompare(data, deployMode, deployMode === 'full_clone' || deployMode === 'replace_data');
      initMetadataSelection(data, deployMode === 'full_clone' || deployMode === 'replace_data');
      if (deployMode === 'full_clone') {
        setIncludeReports(true);
        setIncludeDashboards(true);
        setIncludeHomeLayouts(true);
        setIncludeRoles(true);
      }
      if (direction === 'same' && data?.targetSchemaExists === false) {
        await loadSourceAdminUsers(sourceSchema);
      } else {
        setSourceAdminUsers([]);
        setSourceUserId('');
      }
      setStep('select');
    } catch (e) {
      setError(e?.message || 'Compare failed');
    } finally {
      setCompareLoading(false);
    }
  };

  const selectAllObjects = (checked) => {
    const objs = compare?.objects || [];
    const nextObj = { ...objectChecked };
    const nextFields = { ...fieldChecked };
    const nextFlags = { ...objectFlags };
    for (const obj of objs) {
      nextObj[obj.objectName] = checked;
      const fmap = { ...(nextFields[obj.objectName] || {}) };
      for (const f of obj.fields || []) {
        // Required fields stay checked whenever the object is selected
        fmap[f.fieldName] = checked ? true : false;
        if (checked && isRequiredField(f)) fmap[f.fieldName] = true;
      }
      nextFields[obj.objectName] = fmap;
      nextFlags[obj.objectName] = {
        ...(nextFlags[obj.objectName] || {}),
        includeStructure: true,
        includeConfig: deployMode !== 'structure_only',
        includeData:
          deployMode === 'full_clone' || deployMode === 'replace_data'
            ? checked
            : nextFlags[obj.objectName]?.includeData === true,
      };
    }
    setObjectChecked(nextObj);
    setFieldChecked(nextFields);
    setObjectFlags(nextFlags);
  };

  const selectMissingOnly = () => {
    const objs = compare?.objects || [];
    const nextObj = {};
    const nextFields = {};
    for (const obj of objs) {
      const missingFields = (obj.fields || []).filter((f) => f.status === 'missing');
      const want = !obj.onTarget || missingFields.length > 0;
      nextObj[obj.objectName] = want;
      const fmap = {};
      for (const f of obj.fields || []) {
        fmap[f.fieldName] =
          !obj.onTarget || f.status === 'missing' || (want && isRequiredField(f));
      }
      nextFields[obj.objectName] = fmap;
    }
    setObjectChecked(nextObj);
    setFieldChecked(nextFields);
  };

  const enableDataOnSelected = (on) => {
    setObjectFlags((prev) => {
      const next = { ...prev };
      for (const [name, checked] of Object.entries(objectChecked)) {
        if (!checked) continue;
        next[name] = { ...(next[name] || {}), includeData: on };
      }
      return next;
    });
  };

  const toggleObject = (objectName, checked) => {
    setObjectChecked((prev) => ({ ...prev, [objectName]: checked }));
    if (checked) {
      setExpanded((prev) => ({ ...prev, [objectName]: true }));
      const obj = (compare?.objects || []).find((o) => o.objectName === objectName);
      if (obj) {
        setFieldChecked((prev) => {
          const fmap = { ...(prev[objectName] || {}) };
          for (const f of obj.fields || []) {
            if (fmap[f.fieldName] == null) fmap[f.fieldName] = true;
            if (isRequiredField(f)) fmap[f.fieldName] = true;
          }
          return { ...prev, [objectName]: fmap };
        });
      }
    }
  };

  const toggleAllFields = (objectName, fields, checked) => {
    setFieldChecked((prev) => {
      const fmap = { ...(prev[objectName] || {}) };
      for (const f of fields) {
        if (isRequiredField(f)) {
          fmap[f.fieldName] = true;
        } else {
          fmap[f.fieldName] = checked;
        }
      }
      return { ...prev, [objectName]: fmap };
    });
  };

  const selectedSummary = useMemo(() => {
    const pkg = buildPackageSelection();
    const withFields = pkg.objects.filter((o) => o.fieldNames.length > 0);
    const skipped = pkg.objects.filter((o) => o.fieldNames.length === 0);
    return {
      objectCount: withFields.length,
      fieldCount: withFields.reduce((a, o) => a + o.fieldNames.length, 0),
      dataCount: withFields.filter((o) => o.includeData).length,
      skippedEmpty: skipped.length,
      metadata:
        (pkg.includeReports && (pkg.selectedReportIds || []).length > 0) ||
        (pkg.includeDashboards && (pkg.selectedDashboardIds || []).length > 0) ||
        (pkg.includeHomeLayouts && (pkg.selectedHomeIds || []).length > 0) ||
        (pkg.includeRoles && (pkg.selectedRoleIds || []).length > 0),
      reportCount: (pkg.selectedReportIds || []).length,
      roleCount: (pkg.selectedRoleIds || []).length,
    };
  }, [buildPackageSelection]);

  const setTypeChoice = (key, choice) => {
    setTypeResolutions((prev) => {
      const next = { ...prev, [key]: choice };
      const mismatches = validation?.typeMismatches || [];
      const complete =
        mismatches.length > 0 && mismatches.every((m) => next[m.key] || typeResolutionForce);
      if (complete) {
        // Re-validate after state flush
        setTimeout(() => {
          runValidateWithResolutions(next, typeResolutionForce || null);
        }, 0);
      }
      return next;
    });
    setTypeResolutionForce('');
  };

  const runValidateWithResolutions = async (resolutions, force) => {
    setValidateLoading(true);
    setError('');
    try {
      const base = buildPackageSelection();
      const data = await adminSchemaApi.wizardValidate({
        companyId,
        sourceSchema,
        targetSchema: (direction === 'prod' || direction === 'prod_to_sandbox') ? sourceSchema : targetSchema,
        direction,
        selectedTargetUserId,
        packageSelection: {
          ...base,
          typeResolutions: resolutions || base.typeResolutions,
          typeResolutionForce: force || null,
        },
      });
      setValidation(data?.validation || null);
      if (data?.compare) setCompare(data.compare);
      setStep('validate');
    } catch (e) {
      setError(e?.message || 'Validate failed');
    } finally {
      setValidateLoading(false);
    }
  };

  const forceAllTypeChoices = (choice) => {
    const mismatches = validation?.typeMismatches || [];
    const next = { ...typeResolutions };
    for (const m of mismatches) {
      if (m?.key) next[m.key] = choice;
    }
    setTypeResolutions(next);
    setTypeResolutionForce(choice);
    runValidateWithResolutions(next, choice);
  };

  const runValidate = async () => {
    await runValidateWithResolutions(typeResolutions, typeResolutionForce || null);
  };

  const runDeploy = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const data = await adminSchemaApi.wizardRun({
        companyId,
        sourceSchema,
        targetSchema: (direction === 'prod' || direction === 'prod_to_sandbox') ? sourceSchema : targetSchema,
        direction,
        selectedTargetUserId,
        packageSelection: buildPackageSelection(),
      });
      setResult(data);
      setStep('deploy');
    } catch (e) {
      setError(e?.message || 'Deploy failed');
      if (e?.validation) setValidation(e.validation);
    } finally {
      setRunning(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    setError('');
    try {
      const rows = await adminSchemaApi.wizardHistory(companyId);
      setHistory(Array.isArray(rows) ? rows : []);
      setStep('history');
    } catch (e) {
      setError(e?.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const filteredObjects = useMemo(() => {
    const q = objectFilter.trim().toLowerCase();
    const list = compare?.objects || [];
    if (!q) return list;
    return list.filter(
      (o) =>
        o.objectName.includes(q) ||
        String(o.objectLabel || '').toLowerCase().includes(q)
    );
  }, [compare, objectFilter]);

  const canCompare = !!sourceSchema && (direction === 'prod' || direction === 'prod_to_sandbox' || !!targetSchema) && !compareLoading;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      dialogClassName="admin-deploy-wizard-dialog"
      backdrop={running ? 'static' : true}
    >
      <Modal.Header closeButton={!running} className="admin-wizard-header">
        <div>
          <Modal.Title className="admin-title mb-0">Deploy Wizard</Modal.Title>
          <div className="admin-subtitle small">
            Selective deploy · full clone · truncate+replace · history
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="admin-wizard-body">
        <div className="admin-wizard-steps mb-3">
          {STEPS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`admin-wizard-step ${step === s.key ? 'active' : ''}`}
              disabled={running}
              onClick={() => {
                if (s.key === 'history') loadHistory();
                else if (s.key === 'select' && !compare) return;
                else if (s.key === 'validate' && !validation) return;
                else setStep(s.key);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            {error}
          </Alert>
        )}

        {!selectedTargetUserId &&
          direction === 'same' &&
          deployMode !== 'structure_only' &&
          compare?.targetSchemaExists === true && (
          <Alert variant="warning" className="mb-3 small">
            Target schema already exists — set <strong>Default admin for schema</strong> on the
            company page (incremental deploy ke liye). Naye schema pe ye zaroori nahi: admin
            source se banega (company <strong>Admin Email</strong> + source password).
          </Alert>
        )}

        {direction === 'same' && compare?.targetSchemaExists === false && (
          <Alert variant="info" className="mb-3 small">
            <strong>Naya schema:</strong> pehle company pe <strong>Admin Email</strong> set karo.
            Neeche source se ek <strong>ADMIN user</strong> select karo — uska password target admin pe copy hoga.
            <Form.Group className="mt-2 mb-0">
              <Form.Label className="small fw-semibold">Source ADMIN user (password donor)</Form.Label>
              <Form.Select
                size="sm"
                value={sourceUserId}
                onChange={(e) => setSourceUserId(e.target.value)}
                disabled={running || sourceAdminsLoading}
              >
                <option value="">
                  {sourceAdminsLoading ? 'Loading ADMIN users…' : 'Select source ADMIN user…'}
                </option>
                {sourceAdminUsers.map((u) => {
                  const label = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
                  const who = u.email || u.username || u.id;
                  return (
                    <option key={u.id} value={u.id}>
                      {label ? `${label} — ${who}` : who}
                    </option>
                  );
                })}
              </Form.Select>
              {!sourceAdminsLoading && sourceAdminUsers.length === 0 && (
                <div className="text-warning mt-1">Source me koi ADMIN user nahi mila.</div>
              )}
            </Form.Group>
          </Alert>
        )}

        {step === 'direction' && (
          <>
            <p className="admin-subtitle small mb-2">
              Pehle scenario choose karo — phir direction / schemas. Purane migration buttons alag se kaam karte rehte hain.
            </p>

            <div className="admin-wizard-scenarios mb-3">
              {SCENARIOS.map((sc) => (
                <button
                  key={sc.id}
                  type="button"
                  className={`admin-wizard-scenario ${scenarioId === sc.id ? 'active' : ''}`}
                  onClick={() => applyScenario(sc)}
                  disabled={running}
                >
                  <div className="fw-semibold">{sc.title}</div>
                  <div className="small text-muted">{sc.desc}</div>
                </button>
              ))}
            </div>

            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Direction</Form.Label>
                  <Form.Select
                    value={direction}
                    onChange={(e) => setDirection(e.target.value)}
                    disabled={running}
                  >
                    <option value="same">1 DB → 1 DB (schema → schema)</option>
                    <option value="prod">1 DB → 2 DB (sandbox → production)</option>
                    <option value="prod_to_sandbox">2 DB → 1 DB (production → sandbox)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Source schema</Form.Label>
                  <Form.Select
                    value={sourceSchema}
                    onChange={(e) => setSourceSchema(e.target.value)}
                    disabled={running}
                  >
                    <option value="">Select…</option>
                    {schemas.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Target schema</Form.Label>
                  <Form.Control
                    value={(direction === 'prod' || direction === 'prod_to_sandbox') ? sourceSchema : targetSchema}
                    onChange={(e) => setTargetSchema(e.target.value)}
                    disabled={running || direction === 'prod' || direction === 'prod_to_sandbox'}
                    readOnly={direction === 'prod' || direction === 'prod_to_sandbox'}
                  />
                  {(direction === 'prod' || direction === 'prod_to_sandbox') && (
                    <Form.Text muted>Cross-DB pe schema name source jaisa hi hona chahiye.</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {deployMode === 'full_clone' && direction === 'prod' && (
              <Alert variant="info" className="small mt-3 mb-0">
                <strong>Full clone:</strong> sandbox se production pe structure + config + <em>saara</em> CRM
                data copy hoga (users pehle, phir records as-is — owners purane users pe rehte hain).
              </Alert>
            )}

            <div className="mt-3">
              <Button className="admin-primary-btn" disabled={!canCompare} onClick={runCompare}>
                {compareLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Comparing…
                  </>
                ) : (
                  'Next: Compare & select'
                )}
              </Button>
            </div>
          </>
        )}

        {step === 'select' && compare && (
          <>
            <Alert variant="light" className="border small mb-3">
              <div>
                <strong>{compare.sourceSchema}</strong> → <strong>{compare.targetSchema}</strong>
                {' · '}
                {direction === 'prod' ? '1 DB → 2 DB' : '1 DB → 1 DB'}
                {' · mode '}
                <Badge bg="dark">{deployMode}</Badge>
              </div>
              <div className="text-muted mt-1">
                Diff + selection ek hi screen pe — status badges se missing/partial/same dikhega; checkboxes se choose karo.
              </div>
            </Alert>

            {(compare.warnings || []).length > 0 && (
              <Alert variant="warning" className="small">
                <ul className="mb-0">
                  {compare.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <div className="admin-wizard-compare-stats mb-3">
              <div className="stat">
                <div className="stat-num">{compare.objects?.length || 0}</div>
                <div className="stat-label">Objects</div>
              </div>
              <div className="stat">
                <div className="stat-num text-warning">
                  {(compare.objects || []).filter((o) => o.status === 'missing').length}
                </div>
                <div className="stat-label">Missing on target</div>
              </div>
              <div className="stat">
                <div className="stat-num text-info">
                  {(compare.objects || []).filter((o) => o.status === 'partial').length}
                </div>
                <div className="stat-label">Partial</div>
              </div>
              <div className="stat">
                <div className="stat-num text-danger">
                  {(compare.objects || []).filter((o) => o.status === 'type_mismatch').length}
                </div>
                <div className="stat-label">Type issues</div>
              </div>
              <div className="stat">
                <div className="stat-num text-success">
                  {(compare.objects || []).filter((o) => o.status === 'same').length}
                </div>
                <div className="stat-label">Same</div>
              </div>
            </div>

            <div className="admin-wizard-toolbar mb-3">
              <div className="small text-muted">
                Select what to deploy · {compare.objects?.length || 0} objects in package
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button size="sm" variant="outline-primary" onClick={() => selectAllObjects(true)}>
                  Select all
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => selectAllObjects(false)}>
                  Clear all
                </Button>
                <Button size="sm" variant="outline-info" onClick={selectMissingOnly}>
                  Only missing
                </Button>
                <Button size="sm" variant="outline-success" onClick={() => enableDataOnSelected(true)}>
                  Data ON (selected)
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => enableDataOnSelected(false)}>
                  Data OFF
                </Button>
                <Button size="sm" variant="outline-primary" disabled={compareLoading} onClick={runCompare}>
                  {compareLoading ? 'Refreshing…' : 'Re-compare'}
                </Button>
              </div>
            </div>

            {deployMode === 'replace_data' && (
              <Alert variant="danger" className="small">
                <Form.Check
                  type="checkbox"
                  id="wiz-truncate-confirm"
                  checked={truncateConfirmed}
                  onChange={(e) => setTruncateConfirmed(e.target.checked)}
                  label={
                    <span>
                      Main samajhta/samajhti hoon: selected CRM tables target pe{' '}
                      <strong>TRUNCATE</strong> honge, phir source se naya data aayega.
                    </span>
                  }
                />
              </Alert>
            )}

            <Form.Control
              className="mb-2"
              size="sm"
              placeholder="Filter objects…"
              value={objectFilter}
              onChange={(e) => setObjectFilter(e.target.value)}
            />

            <div className="admin-wizard-meta-row mb-3">
              {[
                {
                  key: 'reports',
                  label: 'Reports',
                  checked: includeReports,
                  setChecked: setIncludeReports,
                  items: compare?.metadataItems?.reports || [],
                  map: selectedReportIds,
                  setMap: setSelectedReportIds,
                },
                {
                  key: 'dashboards',
                  label: 'Dashboards',
                  checked: includeDashboards,
                  setChecked: setIncludeDashboards,
                  items: compare?.metadataItems?.dashboards || [],
                  map: selectedDashboardIds,
                  setMap: setSelectedDashboardIds,
                },
                {
                  key: 'homes',
                  label: 'Home layouts',
                  checked: includeHomeLayouts,
                  setChecked: setIncludeHomeLayouts,
                  items: compare?.metadataItems?.homes || [],
                  map: selectedHomeIds,
                  setMap: setSelectedHomeIds,
                },
                {
                  key: 'roles',
                  label: 'Roles',
                  checked: includeRoles,
                  setChecked: setIncludeRoles,
                  items: compare?.metadataItems?.roles || [],
                  map: selectedRoleIds,
                  setMap: setSelectedRoleIds,
                },
              ].map((g) => {
                const selCount = Object.values(g.map || {}).filter(Boolean).length;
                const open = !!metaExpanded[g.key];
                return (
                  <div key={g.key} className="mb-2 border rounded p-2 bg-white">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        id={`wiz-${g.key}`}
                        label={g.label}
                        checked={g.checked}
                        onChange={(e) => {
                          const on = e.target.checked;
                          g.setChecked(on);
                          if (on && selCount === 0 && (g.items || []).length > 0) {
                            const m = {};
                            for (const row of g.items) m[row.id] = true;
                            g.setMap(m);
                          }
                          if (on) setMetaExpanded((p) => ({ ...p, [g.key]: true }));
                        }}
                      />
                      <span className="small text-muted">
                        {selCount}/{g.items.length} selected
                      </span>
                      <Button
                        size="sm"
                        variant="link"
                        className="py-0 ms-auto"
                        disabled={!g.checked || g.items.length === 0}
                        onClick={() => setMetaExpanded((p) => ({ ...p, [g.key]: !open }))}
                      >
                        {open ? 'Hide list' : 'Choose…'}
                      </Button>
                    </div>
                    {g.checked && open && (
                      <div className="mt-2 ms-1">
                        <div className="d-flex gap-2 mb-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              const m = {};
                              for (const row of g.items) m[row.id] = true;
                              g.setMap(m);
                            }}
                          >
                            All
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => g.setMap({})}
                          >
                            None
                          </Button>
                        </div>
                        <div style={{ maxHeight: 180, overflow: 'auto' }}>
                          {(g.items || []).map((row) => (
                            <Form.Check
                              key={row.id}
                              type="checkbox"
                              className="small"
                              id={`${g.key}-${row.id}`}
                              checked={!!g.map[row.id]}
                              onChange={(e) =>
                                g.setMap((prev) => ({ ...prev, [row.id]: e.target.checked }))
                              }
                              label={
                                <span>
                                  {row.name}
                                  {row.status === 'missing' ? (
                                    <Badge bg="warning" text="dark" className="ms-1">new</Badge>
                                  ) : null}
                                  {row.isAdmin ? (
                                    <Badge bg="info" className="ms-1">admin</Badge>
                                  ) : null}
                                </span>
                              }
                            />
                          ))}
                          {g.items.length === 0 && (
                            <div className="small text-muted">Source me koi item nahi.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="admin-wizard-object-list">
              {filteredObjects.map((obj) => {
                const open = !!expanded[obj.objectName];
                const checked = !!objectChecked[obj.objectName];
                const flags = objectFlags[obj.objectName] || {};
                const selectedFieldCount = Object.values(fieldChecked[obj.objectName] || {}).filter(Boolean).length;
                return (
                  <div key={obj.objectName} className={`admin-wizard-object ${checked ? 'selected' : ''}`}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggleObject(obj.objectName, e.target.checked)}
                        label={
                          <span>
                            <strong>{obj.objectLabel || obj.objectName}</strong>
                            <span className="text-muted small ms-1">({obj.objectName})</span>
                          </span>
                        }
                      />
                      {statusBadge(obj.status)}
                      <span className="small text-muted">
                        {obj.fieldCounts?.total || 0} fields
                        {obj.fieldCounts?.missing ? ` · ${obj.fieldCounts.missing} missing` : ''}
                        {checked ? ` · ${selectedFieldCount} selected` : ''}
                      </span>
                      <Button
                        size="sm"
                        variant="link"
                        className="ms-auto py-0"
                        onClick={() => setExpanded((p) => ({ ...p, [obj.objectName]: !open }))}
                      >
                        {open ? 'Hide fields' : 'Show fields'}
                      </Button>
                    </div>

                    {checked && deployMode !== 'structure_only' && (
                      <div className="d-flex flex-wrap gap-3 mt-2 ms-4 small">
                        <Form.Check
                          type="checkbox"
                          id={`str-${obj.objectName}`}
                          label="Structure"
                          checked={flags.includeStructure !== false}
                          onChange={(e) =>
                            setObjectFlags((p) => ({
                              ...p,
                              [obj.objectName]: { ...flags, includeStructure: e.target.checked },
                            }))
                          }
                        />
                        <Form.Check
                          type="checkbox"
                          id={`cfg-${obj.objectName}`}
                          label="Config"
                          checked={flags.includeConfig !== false}
                          onChange={(e) =>
                            setObjectFlags((p) => ({
                              ...p,
                              [obj.objectName]: { ...flags, includeConfig: e.target.checked },
                            }))
                          }
                        />
                        <Form.Check
                          type="checkbox"
                          id={`data-${obj.objectName}`}
                          label="Data"
                          checked={flags.includeData === true}
                          onChange={(e) =>
                            setObjectFlags((p) => ({
                              ...p,
                              [obj.objectName]: { ...flags, includeData: e.target.checked },
                            }))
                          }
                        />
                      </div>
                    )}

                    {open && (
                      <div className="mt-2">
                        <div className="mb-1">
                          <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => toggleAllFields(obj.objectName, obj.fields || [], true)}>
                            All fields
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => toggleAllFields(obj.objectName, obj.fields || [], false)}>
                            Clear fields
                          </Button>
                        </div>
                        <Table size="sm" bordered hover className="mb-0 admin-wizard-field-table">
                          <thead>
                            <tr>
                              <th style={{ width: 36 }} />
                              <th>Field</th>
                              <th>Type</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(obj.fields || []).map((f) => {
                              const required = isRequiredField(f);
                              const fieldOn = required
                                ? !!checked
                                : !!fieldChecked[obj.objectName]?.[f.fieldName];
                              return (
                              <tr key={f.fieldName}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    disabled={!checked || required}
                                    checked={fieldOn}
                                    title={
                                      required
                                        ? 'Required field — always included when object is selected'
                                        : undefined
                                    }
                                    onChange={(e) => {
                                      if (required) return;
                                      setFieldChecked((p) => ({
                                        ...p,
                                        [obj.objectName]: {
                                          ...(p[obj.objectName] || {}),
                                          [f.fieldName]: e.target.checked,
                                        },
                                      }));
                                    }}
                                  />
                                </td>
                                <td>
                                  {f.fieldLabel || f.fieldName}
                                  {required ? (
                                    <Badge bg="secondary" className="ms-1" title="Required">
                                      required
                                    </Badge>
                                  ) : null}
                                  <div className="text-muted small">{f.fieldName}</div>
                                </td>
                                <td className="small">{f.dataType || '—'}</td>
                                <td>{statusBadge(f.status)}</td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="admin-wizard-summary mt-3">
              <strong>{selectedSummary.objectCount}</strong> objects ·{' '}
              <strong>{selectedSummary.fieldCount}</strong> fields
              {selectedSummary.dataCount ? ` · data on ${selectedSummary.dataCount}` : ''}
              {selectedSummary.reportCount ? ` · ${selectedSummary.reportCount} reports` : ''}
              {selectedSummary.roleCount ? ` · ${selectedSummary.roleCount} roles` : ''}
              {selectedSummary.skippedEmpty ? ` · ${selectedSummary.skippedEmpty} skipped (0 fields)` : ''}
              {selectedSummary.metadata ? ' · metadata' : ''}
              {' · mode '}
              <Badge bg="dark">{deployMode}</Badge>
            </div>

            <div className="mt-3 d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => setStep('direction')}>Back</Button>
              <Button
                className="admin-primary-btn"
                disabled={
                  validateLoading ||
                  (!selectedSummary.objectCount && !selectedSummary.metadata && deployMode !== 'full_clone') ||
                  (deployMode === 'replace_data' && !truncateConfirmed) ||
                  (direction === 'same' &&
                    compare?.targetSchemaExists === false &&
                    !sourceUserId)
                }
                onClick={runValidate}
              >
                {validateLoading ? 'Validating…' : 'Next: Validate'}
              </Button>
            </div>
          </>
        )}

        {step === 'validate' && validation && (
          <>
            <h6 className="mb-2">Preflight · {deployMode}</h6>
            {(validation.typeMismatches || []).length > 0 && (
              <div className="mb-3 border rounded p-3 bg-light">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div>
                    <strong>Type mismatches</strong>
                    <div className="small text-muted">
                      Har field ke liye Source type lo ya Target type rakho. Force all se sab pe ek choice lag sakti hai.
                      Sirf CRM <code>data_type</code> resolve hota hai — PG column ALTER nahi.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-primary" onClick={() => forceAllTypeChoices('source')}>
                      Force all → Source
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => forceAllTypeChoices('target')}>
                      Force all → Target
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-info"
                      disabled={validateLoading}
                      onClick={runValidate}
                    >
                      {validateLoading ? 'Re-checking…' : 'Re-validate'}
                    </Button>
                  </div>
                </div>
                <Table size="sm" bordered responsive className="mb-0 admin-wizard-field-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Source</th>
                      <th>Target</th>
                      <th>PG</th>
                      <th>Use type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.typeMismatches.map((m) => {
                      const choice = typeResolutions[m.key] || m.resolution || '';
                      return (
                        <tr key={m.key}>
                          <td className="small">
                            <code>{m.objectName}.{m.fieldName}</code>
                          </td>
                          <td className="small">{m.sourceDataType || '—'}</td>
                          <td className="small">{m.targetDataType || '—'}</td>
                          <td className="small text-muted">
                            {m.pgTypeMismatch
                              ? `${m.sourcePgType || '?'} → ${m.targetPgType || '?'}`
                              : 'ok'}
                          </td>
                          <td>
                            <Form.Check
                              inline
                              type="radio"
                              name={`type-res-${m.key}`}
                              id={`type-src-${m.key}`}
                              label="Source"
                              checked={choice === 'source'}
                              onChange={() => setTypeChoice(m.key, 'source')}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              name={`type-res-${m.key}`}
                              id={`type-tgt-${m.key}`}
                              label="Target"
                              checked={choice === 'target'}
                              onChange={() => setTypeChoice(m.key, 'target')}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            {validation.ok ? (
              <Alert variant="success">Ready to deploy — no blockers.</Alert>
            ) : (
              <Alert variant="danger">
                <div className="fw-semibold mb-1">Blockers</div>
                <ul className="mb-0">
                  {(validation.blockers || [])
                    .filter((b) => !String(b).includes('Choose Use source or Use target'))
                    .map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  {(validation.typeMismatches || []).some(
                    (m) => !(typeResolutions[m.key] || m.resolution)
                  ) && (
                    <li>
                      Type mismatches unresolved — upar Source / Target choose karo, phir Re-validate.
                    </li>
                  )}
                </ul>
              </Alert>
            )}
            {(validation.warnings || []).length > 0 && (
              <Alert variant="warning">
                <div className="fw-semibold mb-1">Warnings</div>
                <ul className="mb-0">{validation.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
              </Alert>
            )}
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => setStep('select')}>Back</Button>
              <Button
                className="admin-primary-btn"
                disabled={
                  !validation.ok ||
                  running ||
                  (direction === 'same' &&
                    !selectedTargetUserId &&
                    compare?.targetSchemaExists === true &&
                    deployMode !== 'structure_only') ||
                  (direction === 'same' &&
                    compare?.targetSchemaExists === false &&
                    !sourceUserId)
                }
                onClick={runDeploy}
              >
                {running ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deploying…
                  </>
                ) : (
                  'Deploy now'
                )}
              </Button>
            </div>
          </>
        )}

        {step === 'deploy' && (
          <>
            {result ? (
              <Alert variant={result.deploy?.status === 'success' ? 'success' : 'warning'}>
                <div className="fw-semibold">{result.message || 'Deploy finished'}</div>
                <div className="small mt-1">
                  v{result.deploy?.version_no} · {result.deploy?.status} · {result.deploy?.id}
                </div>
              </Alert>
            ) : (
              <Alert variant="secondary">Abhi koi result nahi — Validate ke baad Deploy chalao.</Alert>
            )}
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => setStep('select')}>Again</Button>
              <Button variant="outline-primary" onClick={loadHistory}>History</Button>
            </div>
          </>
        )}

        {step === 'history' && (
          <>
            {historyLoading ? (
              <div className="text-muted">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading…
              </div>
            ) : (
              <Table size="sm" bordered hover responsive className="admin-wizard-field-table">
                <thead>
                  <tr>
                    <th>Ver</th>
                    <th>When</th>
                    <th>Dir</th>
                    <th>Schemas</th>
                    <th>Status</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-muted text-center">No history yet.</td>
                    </tr>
                  ) : (
                    history.map((h) => (
                      <tr key={h.id}>
                        <td>v{h.version_no}</td>
                        <td className="small">{h.started_at ? new Date(h.started_at).toLocaleString() : '—'}</td>
                        <td>{h.direction}</td>
                        <td className="small">{h.source_schema} → {h.target_schema}</td>
                        <td>{statusBadge(h.status)}</td>
                        <td className="small">{h.message || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={running}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
